import { CalendarEvent } from '../context/Data'

/**
 * Interface for overlap details between two events
 */
export interface OverlapDetails {
	existingEvent: CalendarEvent
	overlapStart: Date
	overlapEnd: Date
	existingStart: Date
	existingEnd: Date
	newStart: Date
	newEnd: Date
	// Scenario type for UI display
	scenario:
		| 'new_inside_existing'
		| 'new_overlaps_end'
		| 'new_overlaps_start'
		| 'new_covers_existing'
}

/**
 * Interface for a new slot being created (before it becomes a full CalendarEvent)
 */
export interface NewSlotDraft {
	start: Date
	end: Date
	repeatDays?: string[]
	// Optional RRULE info for new/edited recurring slots.
	// Needed so overlap detection can respect bounded recurrences (rrule.until)
	// when the draft only carries repeatDays + time-of-day.
	rrule?: any
}

/**
 * Helper to get the start date from an event
 */
export const getEventStartDate = (
	event: CalendarEvent | NewSlotDraft
): Date | null => {
	if (event.start) {
		return event.start instanceof Date ? event.start : new Date(event.start)
	} else if ((event as CalendarEvent).rrule?.dtstart) {
		return new Date((event as CalendarEvent).rrule.dtstart)
	}
	return null
}

/**
 * Helper to get the end date from an event
 */
export const getEventEndDate = (
	event: CalendarEvent | NewSlotDraft
): Date | null => {
	if (event.end) {
		return event.end instanceof Date ? event.end : new Date(event.end)
	} else if (
		(event as CalendarEvent).duration?.milliseconds &&
		(event as CalendarEvent).rrule?.dtstart
	) {
		const start = new Date((event as CalendarEvent).rrule.dtstart)
		return new Date(
			start.getTime() + (event as CalendarEvent).duration.milliseconds
		)
	}
	return null
}

/**
 * Gets the repeat days for an event, normalizing 'everyday' to all days
 */
function getRepeatDays(event: CalendarEvent | NewSlotDraft): string[] {
	const repeatDays =
		(event as CalendarEvent).repeatDays ||
		(event as NewSlotDraft).repeatDays ||
		[]

	if (repeatDays.includes('everyday')) {
		return [
			'monday',
			'tuesday',
			'wednesday',
			'thursday',
			'friday',
			'saturday',
			'sunday',
		]
	}

	return repeatDays
}

/**
 * Checks if an event is truly recurring (has repeat days that indicate multiple occurrences)
 */
function isTrulyRecurring(event: CalendarEvent): boolean {
	// Must have repeatDays with actual days to be truly recurring
	const repeatDays = event.repeatDays || []
	return repeatDays.length > 0
}

/**
 * Gets the date range for a recurring event (DTSTART to UNTIL)
 * Returns null if the event is not recurring or doesn't have proper date bounds
 * For events with rrule but no repeatDays, treats them as one-time events (single day range)
 */
function getRecurringDateRange(
	event: CalendarEvent
): { start: Date; end: Date | null } | null {
	if (!event.rrule?.dtstart) {
		return null
	}

	const start = new Date(event.rrule.dtstart)

	// If the event has rrule but is not truly recurring (no repeatDays),
	// treat it as a one-time event - its range is just that single day
	if (!isTrulyRecurring(event)) {
		return { start, end: start }
	}

	const end = event.rrule.until ? new Date(event.rrule.until) : null

	return { start, end }
}

/**
 * Checks if the date ranges of two recurring events overlap
 * Returns true if there's any overlap in their active periods
 * If an event has no UNTIL, it's considered to extend infinitely into the future
 */
function hasOverlappingDateRanges(
	event1: CalendarEvent | NewSlotDraft,
	event2: CalendarEvent
): boolean {
	const calEvent1 = event1 as CalendarEvent
	const range1 = getRecurringDateRange(calEvent1)
	const range2 = getRecurringDateRange(event2)

	// Check if event1 is a NewSlotDraft with repeatDays (new recurring slot being created)
	const isNewSlotRecurring =
		(event1 as NewSlotDraft).repeatDays &&
		(event1 as NewSlotDraft).repeatDays!.length > 0

	// If neither has rrule AND event1 is not a new recurring slot,
	// they're both true one-time events - compare their actual dates
	if (!range1 && !range2 && !isNewSlotRecurring) {
		const start1 = getEventStartDate(event1)
		const start2 = getEventStartDate(event2)
		if (!start1 || !start2) return true

		// Both are one-time events - they only overlap if on the same date
		return (
			start1.getFullYear() === start2.getFullYear() &&
			start1.getMonth() === start2.getMonth() &&
			start1.getDate() === start2.getDate()
		)
	}

	// Get date range for event1 (could be a NewSlotDraft or CalendarEvent)
	let start1: Date
	let end1: Date | null = null

	if (range1) {
		start1 = range1.start
		end1 = range1.end
	} else {
		// It's either a one-time event or a NewSlotDraft
		const startDate = getEventStartDate(event1)
		if (!startDate) return true // Can't determine, assume overlap possible
		start1 = startDate

		// For NewSlotDraft with repeatDays, it's a new recurring event starting from start1
		// For one-time events, start and end are on the same occurrence
		if (isNewSlotRecurring) {
			// New recurring slot - starts at start1, no end (infinite)
			end1 = null
		} else {
			// One-time event
			end1 = startDate
		}
	}

	// Get date range for event2
	let start2: Date
	let end2: Date | null = null

	if (range2) {
		start2 = range2.start
		end2 = range2.end
	} else {
		// event2 has no rrule - check if it's truly recurring via repeatDays
		const startDate = getEventStartDate(event2)
		if (!startDate) return true
		start2 = startDate

		// If event2 has repeatDays, it's recurring; otherwise it's one-time
		if (isTrulyRecurring(event2)) {
			end2 = null // Recurring without rrule.until = infinite
		} else {
			end2 = startDate // One-time event - single day
		}
	}

	// Normalize dates to just the date part (ignore time) for range comparison
	const normalizeDate = (d: Date) => {
		const normalized = new Date(d)
		normalized.setHours(0, 0, 0, 0)
		return normalized
	}

	const startDate1 = normalizeDate(start1)
	const startDate2 = normalizeDate(start2)
	const endDate1 = end1 ? normalizeDate(end1) : null
	const endDate2 = end2 ? normalizeDate(end2) : null

	// Check if date ranges overlap
	// If either end is null (infinite), only check if the other starts before the finite end

	// Case 1: Both have end dates
	if (endDate1 && endDate2) {
		return startDate1 <= endDate2 && startDate2 <= endDate1
	}

	// Case 2: Only event1 has an end date (event2 is infinite)
	if (endDate1 && !endDate2) {
		// event2 starts at startDate2 and goes forever
		// Overlaps if event2 starts before or on event1's end
		return startDate2 <= endDate1
	}

	// Case 3: Only event2 has an end date (event1 is infinite)
	if (!endDate1 && endDate2) {
		// event1 starts at startDate1 and goes forever
		// Overlaps if event1 starts before or on event2's end
		return startDate1 <= endDate2
	}

	// Case 4: Both are infinite - they overlap if their repeat days overlap
	return true
}

/**
 * Checks if two events have overlapping repeat days
 * Returns true if there's at least one common day, or if either is non-recurring
 */
function hasOverlappingDays(
	event1: CalendarEvent | NewSlotDraft,
	event2: CalendarEvent
): boolean {
	const days1 = getRepeatDays(event1)
	const days2 = getRepeatDays(event2)

	// If neither is recurring, check if they're on the same day
	if (days1.length === 0 && days2.length === 0) {
		const start1 = getEventStartDate(event1)
		const start2 = getEventStartDate(event2)
		if (!start1 || !start2) return false

		return (
			start1.getFullYear() === start2.getFullYear() &&
			start1.getMonth() === start2.getMonth() &&
			start1.getDate() === start2.getDate()
		)
	}

	// If one is recurring and the other is not
	if (days1.length === 0 || days2.length === 0) {
		const nonRecurringEvent = days1.length === 0 ? event1 : event2
		const recurringDays = days1.length === 0 ? days2 : days1

		const start = getEventStartDate(nonRecurringEvent)
		if (!start) return false

		const dayOfWeek = start.getDay()
		const dayMap: Record<number, string> = {
			0: 'sunday',
			1: 'monday',
			2: 'tuesday',
			3: 'wednesday',
			4: 'thursday',
			5: 'friday',
			6: 'saturday',
		}

		return recurringDays.includes(dayMap[dayOfWeek])
	}

	// Both are recurring - check for overlapping days
	return days1.some((day) => days2.includes(day))
}

/**
 * Detects if two time slots collide (overlap in time)
 * Handles both one-time events (with start/end) and recurring events (with rrule/duration)
 * For recurring events, also checks if their date ranges (DTSTART to UNTIL) overlap
 */
export const detectCollision = (
	event1: CalendarEvent,
	event2: CalendarEvent
): boolean => {
	// First check if the days overlap
	if (!hasOverlappingDays(event1, event2)) {
		return false
	}

	const start1 = getEventStartDate(event1)
	const end1 = getEventEndDate(event1)
	const start2 = getEventStartDate(event2)
	const end2 = getEventEndDate(event2)

	// If we can't get valid dates, assume no collision
	if (!start1 || !end1 || !start2 || !end2) {
		return false
	}

	// Use isTrulyRecurring to properly handle events with rrule but no repeatDays
	const event1IsRecurring = isTrulyRecurring(event1)
	const event2IsRecurring = isTrulyRecurring(event2)

	if (event1IsRecurring || event2IsRecurring) {
		// First check if the date ranges overlap (considering DTSTART and UNTIL)
		if (!hasOverlappingDateRanges(event1, event2)) {
			return false
		}

		// Extract time-of-day (hours and minutes) from the dates
		const start1Time = start1.getHours() * 60 + start1.getMinutes()
		const end1Time = end1.getHours() * 60 + end1.getMinutes()
		const start2Time = start2.getHours() * 60 + start2.getMinutes()
		const end2Time = end2.getHours() * 60 + end2.getMinutes()

		const timeCollision = start1Time < end2Time && start2Time < end1Time
		return timeCollision
	}

	// For one-time events, compare absolute dates
	const dateCollision = start1 < end2 && start2 < end1
	return dateCollision
}

/**
 * Enhanced collision detection that also considers repeat days for recurring events
 * Only triggers overlap if days, times, AND date ranges all overlap
 */
export const detectCollisionWithDays = (
	newSlot: NewSlotDraft,
	existingEvent: CalendarEvent
): boolean => {
	// First check if the days overlap
	if (!hasOverlappingDays(newSlot, existingEvent)) {
		return false
	}

	const newStart = getEventStartDate(newSlot)
	const newEnd = getEventEndDate(newSlot)
	const existingStart = getEventStartDate(existingEvent)
	const existingEnd = getEventEndDate(existingEvent)

	// If we can't get valid dates, assume no collision
	if (!newStart || !newEnd || !existingStart || !existingEnd) {
		return false
	}

	// Use isTrulyRecurring for existing event to properly handle events with rrule but no repeatDays
	const existingIsRecurring = isTrulyRecurring(existingEvent)
	const newIsRecurring = (newSlot.repeatDays?.length ?? 0) > 0

	const weekdayFromDate = (d: Date) => d.getDay() // 0=Sun ... 6=Sat
	const recurringMatchesDate = (recurring: CalendarEvent, date: Date) => {
		// Prefer repeatDays when available
		const repeatDays = recurring.repeatDays || []
		if (repeatDays.includes('everyday')) return true

		// Map common day strings → JS getDay values used elsewhere in app
		const map: Record<string, number> = {
			sunday: 0,
			monday: 1,
			tuesday: 2,
			wednesday: 3,
			thursday: 4,
			friday: 5,
			saturday: 6,
		}
		const wd = weekdayFromDate(date)
		for (const day of repeatDays) {
			if (map[day] === wd) return true
		}

		// Fallback: byweekday in rrule (if present)
		const by = (recurring.rrule as any)?.byweekday as number[] | undefined
		if (Array.isArray(by) && by.includes(wd)) return true

		return false
	}

	if (existingIsRecurring || newIsRecurring) {
		// For recurring events, first check if their date ranges overlap
		// (e.g., one ends before the other starts based on UNTIL/DTSTART)
		if (!hasOverlappingDateRanges(newSlot, existingEvent)) {
			return false
		}

		// Mixed recurring/one-time: ensure the one-time date is on a day the recurring event runs.
		if (existingIsRecurring && !newIsRecurring) {
			if (!recurringMatchesDate(existingEvent, newStart)) return false
		}
		if (!existingIsRecurring && newIsRecurring) {
			// existing is one-time; check that its weekday is included in the new recurring slot days
			const wd = weekdayFromDate(existingStart)
			const days = newSlot.repeatDays || []
			if (!days.includes('everyday')) {
				const map: Record<string, number> = {
					sunday: 0,
					monday: 1,
					tuesday: 2,
					wednesday: 3,
					thursday: 4,
					friday: 5,
					saturday: 6,
				}
				const ok = days.some((d) => map[d] === wd)
				if (!ok) return false
			}
		}

		// Then compare time-of-day
		const newStartTime = newStart.getHours() * 60 + newStart.getMinutes()
		const newEndTime = newEnd.getHours() * 60 + newEnd.getMinutes()
		const existingStartTime =
			existingStart.getHours() * 60 + existingStart.getMinutes()
		const existingEndTime =
			existingEnd.getHours() * 60 + existingEnd.getMinutes()

		return newStartTime < existingEndTime && existingStartTime < newEndTime
	}

	// For one-time events, compare absolute dates
	return newStart < existingEnd && existingStart < newEnd
}

/**
 * Calculates the overlap details between a new slot and an existing event
 */
export const getOverlapDetails = (
	newSlot: NewSlotDraft,
	existingEvent: CalendarEvent
): OverlapDetails | null => {
	if (!detectCollisionWithDays(newSlot, existingEvent)) {
		return null
	}

	const newStart = getEventStartDate(newSlot)!
	const newEnd = getEventEndDate(newSlot)!
	const existingStart = getEventStartDate(existingEvent)!
	const existingEnd = getEventEndDate(existingEvent)!

	// Effective start/end used for overlap calculations & returned details.
	// For recurring drafts we may project the time-of-day onto a reference day.
	let effectiveNewStart = newStart
	let effectiveNewEnd = newEnd

	// Calculate overlap times
	// For recurring events, we work with time-of-day on the same reference date
	// Use isTrulyRecurring to properly handle events with rrule but no repeatDays
	const existingIsRecurring = isTrulyRecurring(existingEvent)
	const newIsRecurring = (newSlot.repeatDays?.length ?? 0) > 0

	let overlapStart: Date
	let overlapEnd: Date
	let adjustedExistingStart: Date
	let adjustedExistingEnd: Date

	if (existingIsRecurring || newIsRecurring) {
		// When either side is recurring, overlap is based on time-of-day on a shared reference date.
		// IMPORTANT: if ONLY the *new* slot is recurring and the existing event is one-time,
		// the reference date must be the existing event's date. Otherwise we might project the
		// one-time event onto the series start date and accidentally create multi-day spans when trimming.
		const referenceBase =
			newIsRecurring && !existingIsRecurring ? existingStart : newStart
		const referenceDate = new Date(referenceBase)
		referenceDate.setHours(0, 0, 0, 0)

		// Existing: keep its time-of-day
		adjustedExistingStart = new Date(referenceDate)
		adjustedExistingStart.setHours(
			existingStart.getHours(),
			existingStart.getMinutes(),
			0,
			0
		)
		adjustedExistingEnd = new Date(referenceDate)
		adjustedExistingEnd.setHours(
			existingEnd.getHours(),
			existingEnd.getMinutes(),
			0,
			0
		)
		if (adjustedExistingEnd <= adjustedExistingStart) {
			adjustedExistingEnd.setDate(adjustedExistingEnd.getDate() + 1)
		}

		// New: if recurring, project onto reference date; otherwise keep as is.
		const adjustedNewStart = newIsRecurring
			? (() => {
					const d = new Date(referenceDate)
					d.setHours(newStart.getHours(), newStart.getMinutes(), 0, 0)
					return d
				})()
			: newStart
		const adjustedNewEnd = newIsRecurring
			? (() => {
					const d = new Date(referenceDate)
					d.setHours(newEnd.getHours(), newEnd.getMinutes(), 0, 0)
					if (d <= adjustedNewStart) d.setDate(d.getDate() + 1)
					return d
				})()
			: newEnd

		effectiveNewStart = adjustedNewStart
		effectiveNewEnd = adjustedNewEnd

		overlapStart = new Date(
			Math.max(effectiveNewStart.getTime(), adjustedExistingStart.getTime())
		)
		overlapEnd = new Date(
			Math.min(effectiveNewEnd.getTime(), adjustedExistingEnd.getTime())
		)
	} else {
		adjustedExistingStart = existingStart
		adjustedExistingEnd = existingEnd
		overlapStart = new Date(
			Math.max(newStart.getTime(), existingStart.getTime())
		)
		overlapEnd = new Date(Math.min(newEnd.getTime(), existingEnd.getTime()))
	}

	// Determine the overlap scenario
	// IMPORTANT: treat boundary-equality carefully to avoid 0ms duration updates.
	// Example: existing 13-17, new 15-17 should be "new_overlaps_end" (trim existing end),
	// not "new_inside_existing" (which would shift existing start to 17 and create 0 duration).
	let scenario: OverlapDetails['scenario']

	const newStartTime = effectiveNewStart.getTime()
	const newEndTime = effectiveNewEnd.getTime()
	const existingStartTime = adjustedExistingStart.getTime()
	const existingEndTime = adjustedExistingEnd.getTime()

	const startsAtOrAfter = newStartTime >= existingStartTime
	const endsAtOrBefore = newEndTime <= existingEndTime
	const startsBefore = newStartTime < existingStartTime

	if (startsAtOrAfter && endsAtOrBefore) {
		// new is within the existing window (including boundaries)
		if (newStartTime === existingStartTime && newEndTime === existingEndTime) {
			scenario = 'new_covers_existing'
		} else if (newStartTime === existingStartTime) {
			scenario = 'new_overlaps_start'
		} else if (newEndTime === existingEndTime) {
			scenario = 'new_overlaps_end'
		} else {
			scenario = 'new_inside_existing'
		}
	} else if (
		newStartTime <= existingStartTime &&
		newEndTime >= existingEndTime
	) {
		scenario = 'new_covers_existing'
	} else if (startsBefore) {
		scenario = 'new_overlaps_start'
	} else {
		// new starts inside or after existing, and ends after existing
		scenario = 'new_overlaps_end'
	}

	return {
		existingEvent,
		overlapStart,
		overlapEnd,
		existingStart: adjustedExistingStart,
		existingEnd: adjustedExistingEnd,
		newStart: effectiveNewStart,
		newEnd: effectiveNewEnd,
		scenario,
	}
}

/**
 * Finds all events that overlap with a new slot being created
 * Returns array of overlap details for each overlapping event
 */
export const findOverlappingEvents = (
	newSlot: NewSlotDraft,
	allEvents: CalendarEvent[],
	excludeEventId?: string
): OverlapDetails[] => {
	const overlaps: OverlapDetails[] = []

	for (const event of allEvents) {
		// Skip the event being edited (if any)
		if (excludeEventId && event.id === excludeEventId) {
			continue
		}

		const overlapDetails = getOverlapDetails(newSlot, event)
		if (overlapDetails) {
			overlaps.push(overlapDetails)
		}
	}

	return overlaps
}

/**
 * Checks if an event has any collision with events in the list
 */
export const hasCollision = (
	event: CalendarEvent,
	allEvents: CalendarEvent[]
): boolean => {
	return allEvents.some(
		(otherEvent) =>
			otherEvent.id !== event.id && detectCollision(event, otherEvent)
	)
}

/**
 * Calculates priority for an event based on collisions
 * Priority is 0 if no collision, incremented by the number of collisions
 * For example: 0 collisions = priority 0, 1 collision = priority 1, 2 collisions = priority 2, etc.
 */
export const calculatePriority = (
	event: CalendarEvent,
	allEvents: CalendarEvent[]
): number => {
	const collidingEvents = allEvents.filter(
		(otherEvent) =>
			otherEvent.id !== event.id && detectCollision(event, otherEvent)
	)

	const priority = collidingEvents.length

	return priority
}
